import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  MeetingClientVisibilityFilter,
  MeetingVisibilityCountsDto,
} from '@interfaces/meetings/responses/meeting-visibility.response';
import {
  CLIENT_VISIBILITY_FILTER_LABELS,
  CLIENT_VISIBILITY_FILTER_VALUES,
  countForFilter,
} from '@features/dashboard/meetings/components/meeting-visibility-presenter';

/** Uma pílula do filtro rápido "Para o cliente". `value: null` = Todas. */
interface ClientVisibilityChip {
  value: MeetingClientVisibilityFilter | null;
  label: string;
  count: number | null;
  /** Marcador de cor antes do rótulo (protótipo `lista`). */
  marker: 'released' | 'blocked' | 'private' | 'pending' | null;
}

const MARKERS: Partial<Record<MeetingClientVisibilityFilter, ClientVisibilityChip['marker']>> = {
  Released: 'released',
  BlockedByAi: 'blocked',
  Private: 'private',
  Pending: 'pending',
};

/**
 * Filtro rápido "Para o cliente" da lista de Meetings (card 3803): Todas, Liberadas, Barradas pelo
 * Cog, Privadas, Internas ou sem empresa e Cog ainda avaliando, cada uma com a contagem que o
 * backend devolve (`GET meetings/visibility-counts`). Escolhe um estado por vez; quem filtra é a tela.
 */
@Component({
  selector: 'app-meetings-client-visibility-chips',
  standalone: true,
  templateUrl: './meetings-client-visibility-chips.component.html',
  styleUrl: './meetings-client-visibility-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsClientVisibilityChipsComponent {
  /** Estado escolhido; `null` = Todas. */
  readonly selected = input<MeetingClientVisibilityFilter | null>(null);
  /** Contagem do backend; `null` enquanto carrega ou se falhou (a pílula fica sem número). */
  readonly counts = input<MeetingVisibilityCountsDto | null>(null);

  readonly selectedChange = output<MeetingClientVisibilityFilter | null>();

  protected readonly chips = computed<ClientVisibilityChip[]>(() => {
    const counts = this.counts();
    return [
      { value: null, label: 'Todas', count: countForFilter(counts, null), marker: null },
      ...CLIENT_VISIBILITY_FILTER_VALUES.map(value => ({
        value,
        label: CLIENT_VISIBILITY_FILTER_LABELS[value],
        count: countForFilter(counts, value),
        marker: MARKERS[value] ?? null,
      })),
    ];
  });

  protected choose(value: MeetingClientVisibilityFilter | null): void {
    if (value === this.selected()) return;
    this.selectedChange.emit(value);
  }
}
